import { Accordion, ActionIcon, Badge, Box, Card, Group, List, Stack, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { InfoIcon } from "@phosphor-icons/react";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { TermosForm } from "@/features/dashboard/configuracoes/termos/_components/TermosForm";
import { Term, useDeleteTermMutation, useGetTermsByCompanyQuery, useSaveTermMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/termos/")({
  component: RouteComponent
});

function RouteComponent() {
  const { colors } = useMantineTheme();
  const { companyId } = useSelectedCompany();
  const { data, isFetching } = useGetTermsByCompanyQuery({ id: Number(companyId) }, { skip: !companyId });
  const [saveTerm, { isLoading }] = useSaveTermMutation();
  const [deleteTerm, { isLoading: isDeleting }] = useDeleteTermMutation();

  const handleDeleteTerm = (id: number) => {
    modals.openConfirmModal({
      title: "Confirmação de Exclusão",
      children: <Text>Tem certeza que deseja deletar este termo? Esta ação não pode ser desfeita.</Text>,
      labels: { confirm: "Deletar", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteTerm({ id })
    });
  };

  const handleFormSubmit = async (values: { title: string; content: string; active: boolean }) => {
    await saveTerm({ id: null, company: { id: Number(companyId) }, ...values });
  };

  const columns: GridColDef<Term>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "title",
      headerName: "Título",
      display: "flex",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Text fw={500}>{params.value}</Text>
        </Box>
      )
    },
    {
      field: "content",
      headerName: "Conteúdo",
      flex: 2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Text size="sm" lineClamp={2}>
            {params.value.slice(0, 75) + (params.value.length > 75 ? "..." : "")}
          </Text>
        </Box>
      )
    },
    {
      field: "active",
      headerName: "Status",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Badge color={params.value ? "green" : "gray"} variant="light">
          {params.value ? "Ativo" : "Inativo"}
        </Badge>
      )
    },
    {
      field: "updatedAt",
      headerName: "Última Atualização",
      type: "string",
      headerAlign: "center",
      align: "center",
      width: 150
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: (params) => (
        <Group gap="xs">
          <Tooltip label="Editar termo">
            <Link to={`/dashboard/configuracoes/termos/$id/modal`} params={{ id: params.row.id }} className="flex">
              <ActionIcon color="yellow">
                <IconEdit size={16} />
              </ActionIcon>
            </Link>
          </Tooltip>

          <Tooltip label="Deletar termo">
            <ActionIcon color="red" onClick={() => handleDeleteTerm(params.row.id)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      )
    }
  ];

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Title order={2}>Gerenciamento de Termos</Title>
      </Group>

      {/* Formulário para adicionar/editar termo */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <TermosForm onSubmit={handleFormSubmit} />
      </Card>

      {/* Tabela de termos */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Termos Cadastrados</Title>

          <DataGridBase rows={data || []} columns={columns} loading={isFetching || isLoading || isDeleting} />
        </Stack>
      </Card>

      <Accordion>
        <Accordion.Item value="default">
          <Accordion.Control icon={<InfoIcon weight="fill" color={colors.blue[5]} />}>Sobre o Gerenciamento de Termos</Accordion.Control>

          <Accordion.Panel>
            <List type="ordered">
              <List.Item>Os termos definem as regras e políticas da plataforma</List.Item>
              <List.Item>Apenas termos &quot;Ativos&quot; são exibidos aos usuários</List.Item>
              <List.Item>O versionamento automático rastreia as alterações</List.Item>
              <List.Item>Mantenha sempre uma versão ativa dos termos importantes</List.Item>
              <List.Item>O conteúdo dos termos deve ser claro e completo</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
